import { CHANNEL_META, type Channel } from '@str/shared';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';

export function Logo({ size = 19, to = '/' }: { size?: number; to?: string }) {
  const mark = size >= 19 ? 22 : 20;
  return (
    <Link to={to} aria-label="STR Leads Social" className="logo" style={{ fontSize: size }}>
      <img src="/favicon.svg" alt="" width={mark} height={mark} style={{ flexShrink: 0 }} />
      STR Leads
      <span className="logo-product">Social</span>
    </Link>
  );
}

export function Spinner({ light = false }: { light?: boolean }) {
  return <span className={light ? 'spinner light' : 'spinner'} />;
}

export function Check({ size = 18, style }: { size?: number; style?: CSSProperties }) {
  return (
    <span
      className="check"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.55), ...style }}
    >
      ✓
    </span>
  );
}

const STEPS = ['Listing', 'Review', 'Account', 'First post'];
export function StepIndicator({ current }: { current: number }) {
  return (
    <div
      className="hide-md"
      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#777' }}
    >
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <span key={label} style={{ display: 'contents' }}>
            {i > 0 && <span style={{ width: 24, height: 1, background: 'rgba(0,0,0,.15)' }} />}
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 99,
                background: done || active ? '#111' : 'transparent',
                color: done || active ? '#fff' : '#111',
                border: done || active ? 0 : '1px solid rgba(0,0,0,.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {done ? '✓' : n}
            </span>
            <span
              style={{ color: active ? '#111' : undefined, fontWeight: active ? 600 : undefined }}
            >
              {label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function ChannelDots({
  channels,
  size = 6,
  ring = false,
}: {
  channels: readonly Channel[];
  size?: number;
  ring?: boolean;
}) {
  return (
    <span style={{ display: 'flex', gap: 3 }}>
      {channels.map((c) => (
        <span
          key={c}
          className="dot"
          style={{
            width: size,
            height: size,
            background: CHANNEL_META[c].color,
            boxShadow: ring ? '0 0 0 1.5px #fff' : undefined,
          }}
        />
      ))}
    </span>
  );
}

export function Field({
  label,
  children,
  error,
}: {
  label: string;
  children: ReactNode;
  error?: string | null;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {error && <span className="err">{error}</span>}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="err">{children}</div>;
}

export function Pill({
  children,
  dark = false,
  style,
}: {
  children: ReactNode;
  dark?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      className="tag tag-sm"
      style={{ background: dark ? 'rgba(0,0,0,.5)' : 'rgba(0,0,0,.35)', ...style }}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className="card"
      style={{
        padding: 32,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      {body && (
        <div className="muted" style={{ fontSize: 13, maxWidth: 380, lineHeight: 1.45 }}>
          {body}
        </div>
      )}
      {action}
    </div>
  );
}

export function FullScreenSpinner() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 320,
      }}
    >
      <Spinner />
    </div>
  );
}
